import { getApiUrl } from '../config/appConfig';
import api from './api';

export interface IntegrationPingResponse {
  ok: boolean;
  tenantId?: string;
  jti?: string;
  name?: string;
}

export type ValidateIntegrationTokenResult =
  | { success: true; data: IntegrationPingResponse }
  | { success: false; status: number; detail: string };

export interface IntegrationAccessTokenListItem {
  id: number;
  jti: string;
  tenantId: number;
  tenantCode?: string | null;
  tenantName?: string | null;
  name: string;
  createdAt: string;
  createdBy: string;
  expiresAt: string;
  isRevoked: boolean;
  revokedAt?: string | null;
}

export interface CreateIntegrationAccessTokenRequest {
  tenantId: number;
  expiresAt: string;
  name: string;
  scopes?: string | null;
}

export interface IntegrationAccessTokenCreated {
  token: string;
  jti: string;
  expiresAt: string;
  tenantId: number;
  tenantCode?: string | null;
  name: string;
}

const base = '/api/admin/integration-access-tokens';

export function normalizeBearerToken(input: string): string {
  const t = input.trim();
  if (t.toLowerCase().startsWith('bearer ')) {
    return t.slice(7).trim();
  }
  return t;
}

/** Decodifica el payload del JWT sin verificar firma (solo para ayuda en pantalla). */
export function decodeJwtPayloadUnsafe(rawToken: string): Record<string, unknown> | null {
  const token = normalizeBearerToken(rawToken);
  if (!token) return null;
  try {
    const parts = token.split('.');
    if (parts.length !== 3) return null;
    let payload = parts[1].replace(/-/g, '+').replace(/_/g, '/');
    const pad = payload.length % 4;
    if (pad === 2) payload += '==';
    else if (pad === 3) payload += '=';
    const json = atob(payload);
    return JSON.parse(json) as Record<string, unknown>;
  } catch {
    return null;
  }
}

/**
 * Llama a GET /api/integration/ping con el Bearer indicado.
 * Usa fetch (no el cliente axios) para no disparar el refresh de sesión ante un 401.
 */
export async function validateIntegrationTokenWithPing(rawToken: string): Promise<ValidateIntegrationTokenResult> {
  const token = normalizeBearerToken(rawToken);
  if (!token) {
    return { success: false, status: 0, detail: 'Token vacío.' };
  }

  const apiRoot = getApiUrl().replace(/\/$/, '');
  const url = `${apiRoot}/api/integration/ping`;

  const res = await fetch(url, {
    method: 'GET',
    headers: {
      Authorization: `Bearer ${token}`,
      Accept: 'application/json',
    },
  });

  if (res.ok) {
    const data = (await res.json()) as IntegrationPingResponse;
    return { success: true, data };
  }

  const wwwAuth = res.headers.get('WWW-Authenticate');
  let bodySnippet = '';
  try {
    const text = await res.text();
    if (text) {
      try {
        bodySnippet = JSON.stringify(JSON.parse(text));
      } catch {
        bodySnippet = text.length > 300 ? `${text.slice(0, 300)}…` : text;
      }
    }
  } catch {
    /* ignore */
  }

  const parts = [`HTTP ${res.status}`];
  if (bodySnippet) parts.push(bodySnippet);
  if (wwwAuth) parts.push(wwwAuth);

  if (res.status === 403) {
    parts.push(
      'Este endpoint exige el claim token_use=integration. El JWT del login de usuario no lo trae: por eso obtiene 403. Use el token emitido con «Nuevo token» en esta pantalla.'
    );
  } else if (res.status === 401) {
    parts.push(
      '401 suele ser firma, emisor (iss), audiencia (aud) o token revocado/inexistente en Master. Confirme que la apiUrl del front apunta al mismo servidor donde creó el token y revise logs de la API.'
    );
  }

  return { success: false, status: res.status, detail: parts.join(' — ') };
}

export const integrationAccessTokenService = {
  list: async (tenantId?: number): Promise<IntegrationAccessTokenListItem[]> => {
    const response = await api.get<IntegrationAccessTokenListItem[]>(base, {
      params: tenantId != null ? { tenantId } : undefined,
    });
    return response.data;
  },

  create: async (body: CreateIntegrationAccessTokenRequest): Promise<IntegrationAccessTokenCreated> => {
    const response = await api.post<IntegrationAccessTokenCreated>(base, body);
    return response.data;
  },

  revoke: async (id: number): Promise<void> => {
    await api.post(`${base}/${id}/revoke`);
  },

  validateWithPing: validateIntegrationTokenWithPing,
};

export default integrationAccessTokenService;
